import { submitData as coreSubmitData, evaluateTemplateValue } from '@ea-lab/reactive-json'

/**
 * PROJECT OVERRIDE of reactive-json's core `submitData` — l'ENVOI À BLANC du
 * builder. Registered as `reaction.submitData` in builderPlugins (merged after
 * core, so it wins).
 *
 * Pourquoi : dans le builder, le mode « Prévisualiser » rend les formulaires
 * vivants, et leur envoi partait POUR DE VRAI — contact créé ou complété au CRM,
 * soumission, notifications, poussée Mautic, consentement modifié. Tester un
 * formulaire en cours de construction (l'éditeur comme l'assistant) n'a pas à
 * produire tout cela. Aucun YAML ne peut intercepter une action pour tous les
 * blocs à la fois : la capacité manquante est au niveau du moteur.
 *
 * Ce qui est intercepté, quand l'envoi à blanc est actif : un envoi (méthode
 * autre que GET) qui est celui d'un FORMULAIRE DE VISITEUR, reconnu à l'un de
 * ces deux traits —
 *  - il vise un flux ECA (`/eca/…`) : les formulaires de lead, de contact,
 *    d'inscription (21 des 23 formulaires du catalogue, plus 2 flux d'atterrissage) ;
 *  - sa charge porte une preuve anti-robot (`captcha`) : les formulaires publics
 *    servis par une API Orbit (préférences de suivi → `/api/orbit/crm/preferences`).
 * Les lectures en POST (recherche Elasticsearch, facettes) et les écritures d'un
 * écran CRM n'ont ni l'un ni l'autre : elles partent normalement.
 *
 * Qui l'active : `BuilderDryRunPanel`, monté par les seuls éditeurs (page,
 * contenu dans son modèle), pendant l'aperçu. Un compteur de module et non une
 * donnée du bloc : changer le `dataOverride` d'un bloc ré-amorce son store (des
 * saisies non enregistrées y vivent), et un fragment monté par `UnitMount` fige
 * ses données à la résolution. Les pages publiques (rendu SPA, îlots) ne montent
 * jamais ce panneau : le compteur y reste à zéro et le `submitData` du moteur
 * s'exécute tel quel.
 *
 * Ce que l'envoi à blanc rend : la charge EXACTE que le moteur aurait postée
 * (même évaluation), publiée par l'événement `builder:dry-run` sur `window`,
 * puis une réponse `response` simulée sur l'élément cliqué — `{ok: true}` —
 * pour que le formulaire suive son parcours réel (message de confirmation).
 */

export const DRY_RUN_EVENT = 'builder:dry-run'

/** La réponse simulée : celle d'un flux qui a accepté l'envoi. */
export const DRY_RUN_RESPONSE = Object.freeze({ ok: true, status: 'ok', dryRun: true })

// Un compteur, pas un booléen : deux éditeurs montés ensemble (un contenu édité
// dans son modèle au sein d'une page, par exemple) ne s'éteignent pas l'un l'autre.
let holders = 0

/** Allume l'envoi à blanc ; rend la fonction qui le relâche (une seule fois). */
export const holdDryRun = () => {
  holders += 1
  let released = false
  return () => {
    if (released) return
    released = true
    holders = Math.max(0, holders - 1)
  }
}

export const isDryRunActive = () => holders > 0

/** Un flux ECA : une adresse dont le CHEMIN commence par `/eca/` (relative ou absolue). */
export const isFlowEndpoint = (url) => {
  if (typeof url !== 'string' || url === '') return false
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/eca/')
  }
  catch {
    return false
  }
}

const carriesCaptcha = (data) => data !== null && typeof data === 'object' && !Array.isArray(data)
  && Object.prototype.hasOwnProperty.call(data, 'captcha')

export const submitData = (props) => {
  if (!isDryRunActive()) return coreSubmitData(props)

  const { args, globalDataContext, templateContext, eventData } = props ?? {}
  const method = String(args?.httpMethod ?? 'post').toLowerCase()
  if (method === 'get') return coreSubmitData(props)

  const root = globalDataContext?.getRootContext ? globalDataContext.getRootContext() : globalDataContext
  const evaluate = (value) => evaluateTemplateValue({ valueToEvaluate: value, globalDataContext: root, templateContext })

  // Même charge que le moteur (cf. reaction/submitData de reactive-json).
  let data
  if (args && Object.prototype.hasOwnProperty.call(args, 'data')) {
    const raw = args.data
    data = Array.isArray(raw)
      ? raw.map(evaluate)
      : raw !== null && typeof raw === 'object'
        ? Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, evaluate(value)]))
        : evaluate(raw)
    if (root?.templateData?.__state !== undefined && data !== null && typeof data === 'object') data.__state = root.templateData.__state
  }
  else {
    data = { data: root?.templateData }
  }

  const url = evaluate(args?.url)
  if (!isFlowEndpoint(url) && !carriesCaptcha(data)) return coreSubmitData(props)

  window.dispatchEvent(new CustomEvent(DRY_RUN_EVENT, { detail: { url, method, data, at: Date.now() } }))

  // L'élément cliqué se lit MAINTENANT : React remet `currentTarget` à null
  // une fois l'événement traité (le moteur le capture lui aussi d'emblée).
  const target = eventData?.currentTarget
  const requestContext = { url, method, headers: {}, body: data }
  const responseContext = { headers: {}, status: 201, data: DRY_RUN_RESPONSE }
  // Asynchrone comme une vraie requête : les `setData` qui suivent le clic dans
  // la même chaîne sont passés quand la réponse arrive.
  setTimeout(() => {
    target?.dispatchEvent?.(new CustomEvent('response', {
      bubbles: false,
      cancelable: true,
      composed: true,
      detail: { requestContext, value: { ...DRY_RUN_RESPONSE }, responseContext },
    }))
  }, 0)
}
